import { addBusinessDays, addDays, differenceInBusinessDays } from 'date-fns';
import { DateTime } from 'luxon';

import { ReactComponent as CollectionNoteIcon } from 'assets/dialogue-avatar-collection-note.svg';
import {
  extLnkCheckID,
  extLnkCollectionNomination,
  extLnkCollectMissedDelivery,
  extLnkPostOfficeLocator,
} from 'config/track-config';
import { DialogueCardData, DialogueType } from 'models/DialogCard';
import { AddressType, ArticleEvent, CollectionInstruction, Milestone, RedirectStatus } from 'models/shipments';
import { formatJSDate, localJSDateFromISO } from 'utils/date.utils';

import { ArrowLink } from '../Arrows';
import QrCodeCTA from './QrCodeCTA/QrCodeCTA';

const awaitingCollectionDefaults = {
  type: DialogueType.COLLECTION_NOTE,
  icon: <CollectionNoteIcon />,
};

const parcelLockerCard = (date: string, workCentreId: string | null) => ({
  ...awaitingCollectionDefaults,
  analyticsId: 'awaitingCollectionParcelLocker',
  title: 'It’s in your Parcel Locker',
  content: (
    <>
      <p>You can collect it any time before {date}.</p>
      <p>After that time, we’ll transfer it to a nearby Post Office.</p>
    </>
  ),
  actions: <ArrowLink href={extLnkPostOfficeLocator + workCentreId}>View location</ArrowLink>,
});

const poBoxCard = (date: string) => ({
  ...awaitingCollectionDefaults,
  analyticsId: 'awaitingCollectionPOBox',
  title: 'It’s in your PO Box',
  content: (
    <>
      <p>You can collect it any time before the end of {date}.</p>
      <p>After that date, we’ll return it to the sender.</p>
    </>
  ),
});

const anonPostOfficeCard = (date: string, workCentreId: string, trackingId: string, allowDelegate: boolean) => ({
  ...awaitingCollectionDefaults,
  analyticsId: 'anonAwaitingCollectionPostOffice',
  title: 'It’s at the Post Office',
  content: (
    <>
      <p>You can collect it any time before the end of {date}.</p>
      <p>
        The QR code is only for collecting a parcel from a Post Office. To collect from a PO Box, log in to your MyPost
        account for more details.
      </p>
    </>
  ),
  customContent: <QrCodeCTA trackingId={trackingId} />,
  actions: (
    <>
      <ArrowLink href={extLnkPostOfficeLocator + workCentreId}>View location and collection hours</ArrowLink>
      <ArrowLink href={extLnkCheckID}>Check ID requirements</ArrowLink>
      {allowDelegate ? (
        <ArrowLink href={extLnkCollectionNomination(trackingId)}>Let someone else collect it</ArrowLink>
      ) : null}
    </>
  ),
});

const postOfficeCard = (date: string, workCentreId: string, trackingId: string, allowDelegate: boolean) => ({
  ...awaitingCollectionDefaults,
  analyticsId: 'awaitingCollectionPostOffice',
  title: 'It’s at the Post Office',
  content: (
    <>
      <p>You can collect it any time before the end of {date}.</p>
      <p>Show the collection QR code at the counter along with your proof of identity.</p>
    </>
  ),
  customContent: <QrCodeCTA trackingId={trackingId} />,
  actions: (
    <>
      <ArrowLink href={extLnkPostOfficeLocator + workCentreId}>View location and collection hours</ArrowLink>
      <ArrowLink href={extLnkCheckID}>Check ID requirements</ArrowLink>
      {allowDelegate ? (
        <ArrowLink href={extLnkCollectionNomination(trackingId)}>Let someone else collect it</ArrowLink>
      ) : null}
    </>
  ),
});

const failedRedirectCard = (date: string, workCentreId: string, trackingId: string, allowDelegate: boolean) => ({
  ...postOfficeCard(date, workCentreId, trackingId, allowDelegate),
  analyticsId: 'awaitingCollectionFailedRedirection',
  content: (
    <>
      <p>We couldn’t redirect this parcel, so we delivered it to the previous address instead.</p>
      <p>You can collect it any time before the end of {date}.</p>
      <p>Show the collection QR code at the counter along with your proof of identity.</p>
    </>
  ),
});

const collectionReminderCard = (date: string, workCentreId: string, trackingId: string, allowDelegate: boolean) => ({
  ...awaitingCollectionDefaults,
  analyticsId: 'collectionReminder',
  title: `We’ll hold it for you until ${date}`,
  content: (
    <>
      <p>After that date, we’ll return this parcel to the sender.</p>
      <p>Show the collection QR code at the counter along with your proof of identity.</p>
    </>
  ),
  customContent: <QrCodeCTA trackingId={trackingId} />,
  actions: (
    <>
      <ArrowLink href={extLnkPostOfficeLocator + workCentreId}>View location and collection hours</ArrowLink>
      {allowDelegate ? (
        <ArrowLink href={extLnkCollectionNomination(trackingId)}>Let someone else collect it</ArrowLink>
      ) : null}
      <ArrowLink href={extLnkCollectMissedDelivery}>Collection options and info</ArrowLink>
    </>
  ),
});

enum DayThresholds {
  returnFromPostOffice = 10,
  transferFromPoBox = 30,
  transferFromParcelLocker = 2,
  collectionReminder = 5,
}

/**
 * Handles 4 awaiting collection dialogue card scenarios
 * Display logic:
 *    Awaiting collection from Parcel Locker - 48 hrs (2 days) after scan event
 *    Awaiting collection from PO Box - 30 days after scan event
 *    Awaiting collection from Post Office - 1-5 business days after scan event
 *    Awaiting collection from Post Office(reminder) - 6-10 business days after scan event
 */
export function getAwaitingCollectionCard(
  isAuth: boolean,
  articleId: string,
  latestEvent: ArticleEvent | null,
  collectionInstruction: CollectionInstruction | null,
  addressType: AddressType | null,
  redirectStatus?: RedirectStatus | null
): DialogueCardData | null {
  if (!articleId || !latestEvent || !collectionInstruction) return null;

  const { workCentreId: wcid, type } = collectionInstruction.facility || {};
  const allowDelegate = collectionInstruction.delegate?.allowed || false;
  if (!wcid || !type) return null;

  const { milestone, localeDateTime } = latestEvent;
  if (milestone !== Milestone.AWAITING_COLLECTION || !localeDateTime) return null;

  const latestEventDate = localJSDateFromISO(localeDateTime);

  let dateStr: string;
  let transferDate: Date;

  switch (addressType) {
    case AddressType.PARCEL_LOCKER:
      transferDate = addBusinessDays(latestEventDate, DayThresholds.transferFromParcelLocker);
      const parcelLockerExpiry = DateTime.fromJSDate(transferDate);
      const time = parcelLockerExpiry.toFormat('h.mma').toLocaleLowerCase();
      const date = parcelLockerExpiry.toFormat('EEEE d MMMM');
      return isAuth ? parcelLockerCard(`${time} on ${date}`, wcid) : null;
    case AddressType.PO_BOX:
      transferDate = addDays(latestEventDate, DayThresholds.transferFromPoBox);
      dateStr = formatJSDate(transferDate, 'EEEE d MMMM');
      return poBoxCard(dateStr);
    default:
      transferDate = addBusinessDays(latestEventDate, DayThresholds.returnFromPostOffice);
      const daysUntilTransfer = differenceInBusinessDays(transferDate, new Date());
      if (!daysUntilTransfer || daysUntilTransfer < 1) return null;

      dateStr = formatJSDate(transferDate, 'EEEE d MMMM');
      if (!isAuth) {
        const parcelLockerFacilityType = 'Parcel_Locker'.toUpperCase();
        if (type.toUpperCase() !== parcelLockerFacilityType)
          return anonPostOfficeCard(dateStr, wcid, articleId, allowDelegate);
        return null;
      }
      if (daysUntilTransfer <= DayThresholds.collectionReminder)
        return collectionReminderCard(dateStr, wcid, articleId, allowDelegate);

      if (redirectStatus === RedirectStatus.REDIRECT_FAILED)
        return failedRedirectCard(dateStr, wcid, articleId, allowDelegate);

      return postOfficeCard(dateStr, wcid, articleId, allowDelegate);
  }
}
